import { Autocomplete, Stack, TextField } from '@mui/material'
import type { AutocompleteProps } from '@mui/material/Autocomplete'
import { Modal, tokens } from 'ComponentLibrary'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { useCallback, useMemo } from 'react'
import type { NewKpiDraft, ValXmlOption } from '../types'

export type ValXmlFieldDisplayCopy = {
    label: (path: string, fallback: string) => string
    description: (path: string, fallback?: string) => string | undefined
    rentalUsageLabel: (usage: string) => string
}

export const DEFAULT_VAL_XML_FIELD_DISPLAY_COPY: ValXmlFieldDisplayCopy = {
    label: (_, fallback) => fallback,
    description: (_, fallback) => fallback,
    rentalUsageLabel: (usage) => usage,
}

const normaliseOptionDisplayText = (value: string): string =>
    value
        .trim()
        .replace(/[.\s]+$/, '')
        .toLowerCase()

export type AddKpiModalCopy = {
    title: string
    add: string
    cancel: string
    fieldLabel: string
    fieldPlaceholder: string
    valueLabel: string
    fallbackRentalUsage: string
    generalGroupLabel: string
    rentalGroupLabel: (usage: string) => string
    expectedType: (type: string) => string
}

export const DEFAULT_ADD_KPI_MODAL_COPY: AddKpiModalCopy = {
    title: 'Neue Kennzahl hinzufügen',
    add: 'Hinzufügen',
    cancel: 'Abbrechen',
    fieldLabel: 'Kennzahl',
    fieldPlaceholder: 'Kennzahl auswählen',
    valueLabel: 'Wert',
    fallbackRentalUsage: 'Allgemein',
    generalGroupLabel: 'Allgemeine Kennzahlen',
    rentalGroupLabel: (usage) => `Mietaufstellung – ${usage}`,
    expectedType: (type) => `Erwarteter Typ: ${type}`,
}

type AddKpiModalProps = {
    isOpen: boolean
    draft: NewKpiDraft | null
    valXmlOptions: ValXmlOption[]
    valXmlFilter: NonNullable<AutocompleteProps<ValXmlOption, false, false, false>['filterOptions']>
    excludedPaths?: Set<string>
    onClose: () => void
    onConfirm: () => void
    onDraftChange: (updates: Partial<NewKpiDraft>) => void
    copy?: AddKpiModalCopy
    valXmlFields?: ValXmlFieldDisplayCopy
}

export const AddKpiModal = ({
    isOpen,
    draft,
    valXmlOptions,
    valXmlFilter,
    excludedPaths,
    onClose,
    onConfirm,
    onDraftChange,
    copy = DEFAULT_ADD_KPI_MODAL_COPY,
    valXmlFields = DEFAULT_VAL_XML_FIELD_DISPLAY_COPY,
}: AddKpiModalProps) => {
    const selectedOption = draft ? (valXmlOptions.find((option) => option.path === draft.valXmlPath) ?? null) : null
    const excludedNormalized = useMemo(() => {
        if (!excludedPaths || excludedPaths.size === 0) {
            return null
        }
        return new Set(Array.from(excludedPaths).map((path) => path.trim().toLowerCase()))
    }, [excludedPaths])

    const getRawOptionDescription = useCallback((option: ValXmlOption): string | undefined => option.definition.documentation?.trim(), [])
    const getOptionName = useCallback(
        (option: ValXmlOption): string => option.definition.name?.trim() ?? option.path.split('.').pop() ?? option.path,
        []
    )
    const getOptionLabel = useCallback(
        (option: ValXmlOption): string => valXmlFields.label(option.path, getOptionName(option)),
        [getOptionName, valXmlFields]
    )
    const getOptionDescription = useCallback(
        (option: ValXmlOption): string | undefined => valXmlFields.description(option.path, getRawOptionDescription(option)),
        [getRawOptionDescription, valXmlFields]
    )

    const getOptionDisplayValue = useCallback(
        (option: ValXmlOption): string => {
            const label = getOptionLabel(option)
            const description = getOptionDescription(option)
            return description && normaliseOptionDisplayText(description) !== normaliseOptionDisplayText(label)
                ? `${label} · ${description}`
                : label
        },
        [getOptionDescription, getOptionLabel]
    )

    const deriveRentalUsage = useCallback(
        (option: ValXmlOption): string | null => {
            const description = getRawOptionDescription(option)
            if (!description) {
                return null
            }
            if (!description.toLowerCase().startsWith('mietaufstellung:')) {
                return null
            }
            const afterPrefix = description.slice(description.indexOf(':') + 1).trim()
            if (!afterPrefix) {
                return copy.fallbackRentalUsage
            }
            const usageMatch = afterPrefix.match(/^(.*?)\s+(Vermietet|Leerstand|Let|Vacant|Contractual|Estimated|Anzahl)/i)
            const rawUsage = (usageMatch ? usageMatch[1] : afterPrefix).trim()
            return rawUsage.length > 0 ? valXmlFields.rentalUsageLabel(rawUsage) : copy.fallbackRentalUsage
        },
        [copy.fallbackRentalUsage, getRawOptionDescription, valXmlFields]
    )

    const isRentalOption = useCallback((option: ValXmlOption): boolean => deriveRentalUsage(option) !== null, [deriveRentalUsage])
    const getGroupLabel = useCallback(
        (option: ValXmlOption): string => {
            const usage = deriveRentalUsage(option)
            if (usage) {
                return copy.rentalGroupLabel(usage)
            }
            return copy.generalGroupLabel
        },
        [copy, deriveRentalUsage]
    )

    const sortOptions = useCallback(
        (options: ValXmlOption[]): ValXmlOption[] =>
            [...options].sort((a, b) => {
                const aIsRental = isRentalOption(a)
                const bIsRental = isRentalOption(b)
                if (aIsRental !== bIsRental) {
                    return aIsRental ? 1 : -1
                }
                if (!aIsRental && !bIsRental) {
                    return getOptionDisplayValue(a).localeCompare(getOptionDisplayValue(b), 'de', { sensitivity: 'base' })
                }
                const usageComparison = getGroupLabel(a).localeCompare(getGroupLabel(b), 'de', { sensitivity: 'base' })
                if (usageComparison !== 0) {
                    return usageComparison
                }
                return getOptionDisplayValue(a).localeCompare(getOptionDisplayValue(b), 'de', { sensitivity: 'base' })
            }),
        [getGroupLabel, getOptionDisplayValue, isRentalOption]
    )

    const filteredOptions = useMemo(() => {
        if (!excludedNormalized || excludedNormalized.size === 0) {
            return sortOptions(valXmlOptions)
        }
        const selectedPath = selectedOption?.path?.trim().toLowerCase()
        const filtered = valXmlOptions.filter((option) => {
            const normalized = option.path.trim().toLowerCase()
            if (selectedPath && normalized === selectedPath) {
                return true
            }
            return !excludedNormalized.has(normalized)
        })
        return sortOptions(filtered)
    }, [excludedNormalized, selectedOption?.path, sortOptions, valXmlOptions])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={copy.title}
            width="500px"
            footerProps={{
                primaryButton: {
                    text: copy.add,
                    onClick: onConfirm,
                },
                secondaryButton: {
                    text: copy.cancel,
                    onClick: onClose,
                },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing200} width="100%">
                <Autocomplete
                    fullWidth
                    disablePortal
                    options={filteredOptions}
                    filterOptions={valXmlFilter}
                    autoHighlight
                    value={selectedOption}
                    onChange={(_, option) => onDraftChange({ valXmlPath: option?.path ?? '' })}
                    renderInput={(params) => <TextField {...params} label={copy.fieldLabel} placeholder={copy.fieldPlaceholder} required />}
                    groupBy={(option) => getGroupLabel(option)}
                    getOptionLabel={(option) => {
                        return getOptionLabel(option)
                    }}
                    renderOption={(props, option) => {
                        const displayValue = getOptionDisplayValue(option)
                        return (
                            <li {...props} key={option.path}>
                                <TextWithEllipsis tooltipText={displayValue} typographyProps={{ variant: 'textS' }}>
                                    {displayValue}
                                </TextWithEllipsis>
                            </li>
                        )
                    }}
                />
                <TextField
                    label={copy.valueLabel}
                    fullWidth
                    required
                    value={draft?.value ?? ''}
                    onChange={(event) => onDraftChange({ value: event.target.value })}
                    helperText={
                        selectedOption?.definition?.baseType
                            ? copy.expectedType(selectedOption.definition.baseType?.replace('xs:', ''))
                            : undefined
                    }
                    FormHelperTextProps={{ sx: { color: tokens.ColorTextSubtle } }}
                />
            </Stack>
        </Modal>
    )
}
